import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type { PairingResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The partner finder's PROPOSE mutation (SLICE 1.4-c) — POST
 * /tournaments/draws/{id}/pairings via tournamentApi.proposePairing, body
 * { partner_entry_id } (the chosen candidate's own entry_id, forwarded VERBATIM).
 * Thin transport: the hook owns no business logic — it sends the entry id and
 * lets the backend create the `proposed` pairing.
 *
 * On SUCCESS it invalidates:
 *   - pairingsMine(drawId) — the proposer's inbox now has the new outgoing
 *     proposal, so it refetches.
 *   - partnerCandidates(drawId) — the NO-ARG invalidation PREFIX (matches every
 *     q-variant), so the candidate pool refetches and the proposed-to candidate
 *     drops out of the available set regardless of the active search.
 *
 * It does NOT invalidate registrationsMine: a PROPOSE is not an accept — no fold
 * happens, no entry is created/changed, so the player's registrations are
 * untouched (only respond/accept folds and would invalidate registrations).
 */
export function useProposePairing(drawId: string) {
  const queryClient = useQueryClient();
  return useMutation<PairingResponse, unknown, { partnerEntryId: string }>({
    mutationFn: ({ partnerEntryId }) =>
      tournamentApi.proposePairing(drawId, {
        partner_entry_id: partnerEntryId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.pairingsMine(drawId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.partnerCandidates(drawId),
      });
    },
  });
}
